import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';

import { ResourcesEditor } from '#screens/resources-editor/resources-editor.js';
import { useTRPC } from '#src/trpc.js';
import { useAutoSave } from '#utils/use-auto-save/use-auto-save.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';

import type { IComponentResource } from '@cadstrom/sigma-engine-shared';
import type { SetStateAction } from 'react';

export const Route = createFileRoute(
	'/_authorization-gate/package-store/$packageFamilyId/packages/$packageId/resources/',
)({
	staticData: {
		navigation: {
			label: 'Package: Resources',
			keywords: ['resources'],
		},
	},
	loader: ({ context: { queryClient, getPackageOptions } }) => {
		void queryClient.prefetchQuery(getPackageOptions);
		const cached = queryClient.getQueryData(getPackageOptions.queryKey);
		return { pkg: cached?.pkg };
	},
	head: ({ loaderData }) => ({
		meta: [
			{
				title:
					(loaderData?.pkg ? loaderData.pkg.manufacturerPartNumber + ': ' : '') +
					'Resources • Package Editor • Cadstrom',
			},
		],
	}),
	component: ResourcesTab,
});

function ResourcesTab() {
	const queryClient = useQueryClient();
	const { getPackageOptions } = Route.useRouteContext();
	const trpc = useTRPC();

	const {
		data: { pkg },
	} = useSuspenseQuery(getPackageOptions);

	const { mutateAsync: updatePackage, isPending: updatePackageIsPending } = useMutation(
		trpc.package.update.mutationOptions({
			onSuccess: (res) => {
				// Merge rather than replace: `isConfidential` is derived from the package family's owner and
				// is not in the mutation response, so overwriting the entry would drop it and hide the badge.
				queryClient.setQueryData(getPackageOptions.queryKey, (previous) =>
					previous === undefined ? undefined : { ...previous, pkg: res.pkg },
				);

				setServerState({ resources: res.pkg.resources, functions: res.pkg.functions });
			},
		}),
	);

	const {
		draftState: { resources: draftResources },
		setDraftState: setDraftResources,
		setServerState,
		isDirty,
		handleSave,
		saveAreaContainerRef,
	} = useAutoSave({
		initialState: {
			resources: pkg.resources,
			functions: pkg.functions,
		},
		mutationKeys: trpc.package.update.mutationKey(),
		saveFn: async (draftData) => {
			await updatePackage({
				id: pkg.id,
				data: {
					resources: draftData.resources,
					functions: { set: draftData.functions },
				},
			});
		},
	});

	const handleUpdateResources = useCallback(
		(updater: SetStateAction<IComponentResource[]>) => {
			const updatedResources = typeof updater === 'function' ? updater(draftResources) : updater;
			setDraftResources((prev) => ({
				...prev,
				resources: updatedResources,
				// Remove any references to removed resources:
				functions: prev.functions.map((func) => ({
					...func,
					resourceConsumed:
						updatedResources.find((res) => res.name === func.resourceConsumed) ? func.resourceConsumed : null,
				})),
			}));
		},
		[draftResources, setDraftResources],
	);

	const el = useDataElTagGenerator(ResourcesTab);
	return (
		<div
			{...el()}
			ref={saveAreaContainerRef}
			id={ResourcesTab.name}
			className='flex min-h-0 grow basis-0 flex-col px-3 py-2'
		>
			<ResourcesEditor
				resources={draftResources}
				onChange={handleUpdateResources}
				showSaveButton={{
					saveDisabled: !isDirty,
					onClickSave: handleSave,
					isSaving: updatePackageIsPending,
				}}
			/>
		</div>
	);
}
